import axios from 'axios';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { setupAuthInterceptor, retryWithBackoff } from '../common';
import { NAMESPACES } from '../configuration';

const BASE_URL = NAMESPACES.configuration_api.local_plans;

const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Cached API wrapper for this namespace/group
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'configuration_api',
  group: 'local_plans',
  defaultTtl: '5m',
  hashLargeSegments: true,
  emitMeta: true,
});

// Types
export interface LocalPlan {
  local_plan_id: number;
  local_id: number;
  agreed_price: number;
  month_day_billing: number;
  visits_number?: number;
  contract_start_date: string;
  trial_start_date?: string;
  trial_duration_days?: number;
  is_active: boolean;
  canceled_at?: string;
  cancellation_reason?: string;
  local_plans_metadata?: any;
}

export interface CreateLocalPlanParams {
  agreed_price: number;
  month_day_billing: number;
  visits_number?: number;
  contract_start_date?: string;
  trial_start_date?: string;
  trial_duration_days?: number;
  local_plans_payments_metadata?: any;
}

export interface UpdateLocalPlanParams {
  agreed_price?: number;
  month_day_billing?: number;
  visits_number?: number;
  reason?: string;
  updated_by_user_id?: number;
}

export interface CancelLocalPlanParams {
  reason: string;
  notes?: string;
}

// API Functions

/**
 * Get all local plans
 */
export const getAllLocalPlans = async () => {
  return await retryWithBackoff(() =>
    cachedApi.get('/GetAllLocalPlans')
  );
};

/**
 * Create a new plan for a local
 */
export const createLocalPlan = async (localId: number, params: CreateLocalPlanParams) => {
  const res = await retryWithBackoff(() =>
    api.post(`/CreateLocalPlan/${localId}`, params)
  );
  await cachedApi.clear();
  return res;
};

/**
 * Get a local plan by its ID
 */
export const getLocalPlanById = async (localPlanId: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetLocalPlanById/${localPlanId}`)
  );
};

/**
 * Get the current active plan for a local
 */
export const getLocalPlanByLocalId = async (localId: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetLocalPlanByLocalId/${localId}`)
  );
};

/**
 * Update a local plan by its ID
 */
export const updateLocalPlanById = async (localPlanId: number, params: UpdateLocalPlanParams) => {
  const res = await retryWithBackoff(() =>
    api.put(`/UpdateLocalPlanById/${localPlanId}`, params)
  );
  await cachedApi.clear();
  return res;
};

/**
 * Update the active plan for a local
 */
export const updateLocalPlanByLocalId = async (localId: number, params: UpdateLocalPlanParams) => {
  const res = await retryWithBackoff(() =>
    api.put(`/UpdateLocalPlanByLocalId/${localId}`, params)
  );
  await cachedApi.clear();
  return res;
};

/**
 * Cancel a local plan by its ID (churn)
 */
export const cancelLocalPlanById = async (localPlanId: number, params: CancelLocalPlanParams) => {
  const res = await retryWithBackoff(() =>
    api.put(`/CancelLocalPlanById/${localPlanId}`, params)
  );
  await cachedApi.clear();
  return res;
};

/**
 * Cancel the active plan for a local (churn)
 */
export const cancelLocalPlanByLocalId = async (localId: number, params: CancelLocalPlanParams) => {
  const res = await retryWithBackoff(() =>
    api.put(`/CancelLocalPlanByLocalId/${localId}`, params)
  );
  await cachedApi.clear();
  return res;
};

/**
 * Reactivate a canceled local plan by its ID
 */
export const reactivateLocalPlanById = async (localPlanId: number) => {
  const res = await retryWithBackoff(() =>
    api.put(`/ReactivateLocalPlanById/${localPlanId}`)
  );
  await cachedApi.clear();
  return res;
};

/**
 * Reactivate the most recent canceled plan for a local
 */
export const reactivateLocalPlanByLocalId = async (localId: number) => {
  const res = await retryWithBackoff(() =>
    api.put(`/ReactivateLocalPlanByLocalId/${localId}`)
  );
  await cachedApi.clear();
  return res;
};

/**
 * Delete a local plan by its ID
 */
export const deleteLocalPlan = async (localPlanId: number) => {
  const res = await retryWithBackoff(() =>
    api.delete(`/DeleteLocalPlan/${localPlanId}`)
  );
  await cachedApi.clear();
  return res;
};
